import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Wand2,
  BookOpen,
  Users,
  ArrowLeft,
  Calendar,
  Mail,
  Phone,
} from 'lucide-react';
import { SiteFooter } from './SiteFooter';
import { Reflections } from './Reflections';

const SERVICE_PILLARS = [
  {
    icon: Sparkles,
    title: 'Marketing Tarot Reading',
    subtitle: 'A strategic diagnostic experience',
    desc: 'A strategic diagnostic experience that reveals where your brand stands, what it needs, and where to move next.',
  },
  {
    icon: Wand2,
    title: 'Brand Alchemy',
    subtitle: 'Turning ideas into powerful brands',
    desc: 'Turning ideas into powerful brands — clarifying purpose, positioning, and the story only you can tell.',
  },
  {
    icon: BookOpen,
    title: 'Storytelling Alchemy',
    subtitle: 'Making brands memorable',
    desc: 'Making brands memorable — narrative, voice, and content that connect with the people who matter.',
  },
  {
    icon: Users,
    title: 'Community & Influence Alchemy',
    subtitle: 'Partnership strategy that builds trust and reach',
    desc: 'Partnership and community strategy that builds trust, credibility, and reach with intention.',
  },
];

export const ConsultingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="consulting-back-btn"
        >
          <ArrowLeft className="w-4 h-4 hidden" />
          Back to Home
        </button>
        <a
          href="https://calendly.com/marketingtarot/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-5 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest shadow-lg shadow-[#e89b47]/25 rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
          data-testid="book-clarity-call-btn"
        >
          Book a Clarity Call
        </a>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-20 text-center">
        <p className="font-display text-sm tracking-[0.3em] text-[#e89b47] mb-6 uppercase">
          Consulting
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#e8dcc8] leading-tight mb-6 tracking-wider" data-testid="consulting-headline">
          MARKETING <span className="text-[#e89b47]">ALCHEMY</span>
        </h1>
        <p className="font-display text-lg sm:text-xl lg:text-2xl text-[#e89b47]/90 italic leading-snug max-w-2xl mx-auto" data-testid="consulting-quote">
          &ldquo;Marketing alchemy is where intuition meets execution &mdash; content, community and conversion.&rdquo;
        </p>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#e89b47]/30 to-transparent" />
      </div>

      {/* Section 1 — What I Do */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="font-display text-2xl sm:text-3xl text-[#e8dcc8] mb-8 text-center tracking-wider" data-testid="what-i-do-heading">
          WHAT WE DO
        </h2>
        <div className={cn(
          "p-8 rounded-2xl",
          "bg-[#1e2a4a]/50 border border-[#d4c4a8]/15"
        )}>
          <p className="font-body text-[#d4c4a8]/80 leading-relaxed">
            Marketing isn&rsquo;t business &mdash; it&rsquo;s people&rsquo;s lives, decisions, emotions, and behaviors. Most teams don&rsquo;t need more noise; they need a partner who can see the whole board: the story, the audience, the emotional triggers, the cultural context, and the execution path. I work with founders, artistic organizations, and creative teams who want strategy that feels <span className="text-[#e89b47]">human</span> &mdash; and performs.
          </p>
        </div>
      </section>

      {/* Section 2 — Service Pillars */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <h2 className="font-display text-2xl sm:text-3xl text-[#e8dcc8] mb-10 text-center tracking-wider" data-testid="services-heading">
          HOW CAN WE HELP
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICE_PILLARS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className={cn(
                  "group p-6 rounded-2xl transition-all duration-300",
                  "bg-[#1e2a4a]/40 border border-[#d4c4a8]/10",
                  "hover:border-[#e89b47]/40 hover:shadow-lg hover:shadow-[#e89b47]/5"
                )}
                data-testid={`service-pillar-${i}`}
              >
                <div className={cn(
                  "w-11 h-11 rounded-xl flex items-center justify-center mb-4",
                  "bg-[#e89b47]/10 border border-[#e89b47]/30",
                  "group-hover:bg-[#e89b47]/20 transition-colors"
                )}>
                  <Icon className="w-5 h-5 text-[#e89b47]" />
                </div>
                <h3 className="font-display text-base text-[#e8dcc8] mb-1">{s.title}</h3>
                {s.subtitle && (
                  <p className="font-body text-xs italic text-[#e89b47]/80 mb-2">{s.subtitle}</p>
                )}
                <p className="font-body text-sm text-[#d4c4a8]/60 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#d4c4a8]/15 to-transparent" />
      </div>

      {/* Section 3 — What It's Like (removed per request) */}

      {/* Testimonials */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        <Reflections className="mt-0" />
      </div>

      {/* Section 4 — Social Proof (removed per request) */}

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#e89b47]/30 to-transparent" />
      </div>

      {/* Section 5 — How We Start */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="font-display text-2xl sm:text-3xl text-[#e8dcc8] mb-10 text-center tracking-wider" data-testid="how-we-start-heading">
          HOW WE START
        </h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            {
              icon: Calendar,
              title: 'Book a 20-min Clarity Call',
              desc: 'Pick a time that works. We\u2019ll talk goals, audience, and what\u2019s in the way.',
              cta: 'Book on Calendly',
              href: 'https://calendly.com/marketingtarot/30min',
              external: true,
              testid: 'how-we-start-book',
            },
            {
              icon: Mail,
              title: 'Email Us',
              desc: 'Prefer to write first? Send a note and we\u2019ll get back within a day.',
              cta: 'Clarity@themarketingtarot.com',
              href: 'mailto:Clarity@themarketingtarot.com',
              external: false,
              testid: 'how-we-start-email',
            },
            {
              icon: Phone,
              title: 'Call Us',
              desc: 'Old-fashioned? Give us a ring \u2014 we\u2019d love to hear from you.',
              cta: '+1 (347) 285-9992',
              href: 'tel:+13472859992',
              external: false,
              testid: 'how-we-start-call',
            },
          ].map((opt, i) => {
            const Icon = opt.icon;
            return (
              <div
                key={i}
                className={cn(
                  'group text-center p-6 rounded-2xl transition-all duration-300',
                  'bg-[#1e2a4a]/40 border border-[#d4c4a8]/10',
                  'hover:border-[#e89b47]/40 hover:shadow-lg hover:shadow-[#e89b47]/5'
                )}
                data-testid={opt.testid}
              >
                <div className={cn(
                  'w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center',
                  'bg-[#e89b47]/10 border-2 border-[#e89b47]/40 group-hover:bg-[#e89b47]/20 transition-colors'
                )}>
                  <Icon className="w-6 h-6 text-[#e89b47]" />
                </div>
                <h3 className="font-display text-base text-[#e8dcc8] mb-2">{opt.title}</h3>
                <p className="font-body text-sm text-[#d4c4a8]/60 leading-relaxed mb-4">{opt.desc}</p>
                <a
                  href={opt.href}
                  {...(opt.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="inline-flex items-center gap-1.5 font-display text-xs tracking-widest uppercase text-[#e89b47] hover:text-[#e8dcc8] transition-colors break-all"
                  data-testid={`${opt.testid}-cta`}
                >
                  {opt.cta}
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section 6 — Tarot Angle (removed per request) */}

      {/* Section 7 — Final Nudge (removed per request) */}

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

export default ConsultingPage;
